import './App.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppRouter } from '@common/router';
import { globalStyles } from '@/stitches.config';
import {
  ErrorBoundary,
  ErrorTrackerProvider,
  initErrorTracker,
  type ErrorTrackerLogger,
} from '@myhelix/dd-error-tracking';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { errorTrackerConfig } from '@/config';

import Notifications from '@modules/notifications/components/Notifications/Notifications';
import Auth from '@modules/auth/components/Auth/Auth';
import FeatureFlags from '@modules/feature-flags/components/FeatureFlags/FeatureFlags';
import ClientInterceptors from './modules/core/components/ClientInterceptors/ClientInterceptors';
import ErrorScreen from '@modules/core/views/ErrorScreen';
import { useAuthStore } from './modules/auth/store';
import LoggingOut from './modules/core/components/LoggingOut/LoggingOut';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// initErrorTracker throws synchronously if any required field (clientToken, service, env,
// version, ...) is missing or invalid — e.g. VITE_CIRCLE_SHA1 (version) is only set by CI, so
// local dev would otherwise crash before React ever mounts. Disable tracking instead of crashing.
let errorTrackerLogger: ErrorTrackerLogger | null = null;
try {
  if (errorTrackerConfig.clientToken) {
    errorTrackerLogger = initErrorTracker(errorTrackerConfig);
    // @myhelix/dd-error-tracking never sets `reportableError`, which the org-wide Error
    // Tracking inclusion rule (@reportableError:true) requires — without this, captured
    // errors reach Logs but never become ET issues or page anyone. Stopgap until the
    // package itself supports it.
    datadogLogs.setGlobalContextProperty('reportableError', true);
    if (errorTrackerConfig.applicationId) {
      datadogRum.setGlobalContextProperty('reportableError', true);
    }
  }
} catch (error) {
  // eslint-disable-next-line no-console
  console.error('Failed to initialize Datadog error tracking', error);
}

const App: React.FC = () => {
  const { isLoggingOut } = useAuthStore();

  globalStyles();

  const content = (
    <Auth>
      <ClientInterceptors>
        <QueryClientProvider client={queryClient}>
          <FeatureFlags>
            {isLoggingOut && <LoggingOut />}
            {!isLoggingOut && (
              <>
                <Notifications />
                <AppRouter />
              </>
            )}
          </FeatureFlags>
        </QueryClientProvider>
      </ClientInterceptors>
    </Auth>
  );

  return (
    <ErrorBoundary fallback={ErrorScreen}>
      {errorTrackerLogger ? (
        <ErrorTrackerProvider logger={errorTrackerLogger}>{content}</ErrorTrackerProvider>
      ) : (
        content
      )}
    </ErrorBoundary>
  );
};

export default App;
